import { Component, Fragment, h, Host, Prop, State } from '@stencil/core';
import { add, format, getDate, getDay, isSameDay } from 'date-fns';
import { DayState, DayConfig } from '../../../../types/types';
import { t } from '../../../../redux/utils';
import { CalendarMode } from '../../rentware-date-select/utils';
import { ClickOutside } from '../../../../utils/stencil/clickOutside';

@Component({
  tag: 'rentware-range-picker-day',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareRangePickerDay {
  @Prop() day!: DayConfig;
  @Prop() index!: number;
  @Prop() secondMonth!: boolean;
  @Prop() allowPast!: boolean;
  @Prop() withDateRange!: boolean;
  @Prop() calendarMode!: CalendarMode;
  @Prop() lastAvailableDate!: Date;
  @Prop() bookingDate!: Date;
  @Prop() endDate!: Date;
  @Prop() firstDate!: Date;
  @Prop() firstAvailableDate: Date;
  @Prop() firstDayOfWeek!: number;
  @Prop() onNextClick!: () => void;
  @Prop() onDayClick!: (day: DayConfig) => void;
  @Prop() onDayHover!: (day: DayConfig) => void;

  @State() showTooltip: boolean;

  @ClickOutside()
  handleOutside() {
    this.showTooltip = false;
  }

  firstDayColumn = (secondMonth: boolean = false) => {
    const firstDay: number = secondMonth ? getDay(add(this.firstDate, { months: 1 })) : getDay(this.firstDate);

    let firstDayColumn: number;

    if (firstDay >= this.firstDayOfWeek) {
      firstDayColumn = firstDay - this.firstDayOfWeek + 1;
    } else {
      const zeroDayColumn = 7 - this.firstDayOfWeek + 1;
      firstDayColumn = zeroDayColumn + firstDay;
    }

    return firstDayColumn;
  };

  render() {
    const date = this.day.date;
    const allowPast = this.allowPast;
    const daySelectable = this.day.dayState === DayState.Selectable || this.day.dayState === DayState.SelectablePartially;

    const inRange = this.day.isInRange || this.day.isInHoverRange;
    const available = daySelectable && !inRange;
    const notAllowedCondition = this.day.dayState === DayState.Through && !inRange;
    const notAvailableCondition = this.day.dayState === DayState.Blocked && !inRange && !this.day.inPast && !this.day.clickToReset;
    const notAllowed = this.withDateRange || this.calendarMode === CalendarMode.SingleDayPickup ? notAllowedCondition : false;
    const notAvailable = notAvailableCondition || (notAllowedCondition && this.calendarMode === CalendarMode.SingleDayDropOff);

    const dayInRange = inRange && daySelectable;
    const notAllowedHoverRange = inRange && this.day.dayState === DayState.Through && !this.day.isPickupDate;
    const notAvailableHoverRange = inRange && this.day.dayState === DayState.Blocked && !this.day.isPickupDate;
    const nextDayAvailableForDropOff = !isSameDay(date, this.lastAvailableDate);
    const lastHoverRange = dayInRange && !nextDayAvailableForDropOff;

    // not available day can be selected:
    // 1. today for slots - default at start - not clickable again when changed
    // 2. selected pick-up that becomes as unavailable day for drop-off
    const selected = this.day.selected && (((available || this.day.isToday) && !this.withDateRange) || this.withDateRange);
    const hovered = this.day.isDropHoverOffDate && daySelectable;
    const empty =
      (this.day.inPast && !allowPast) || // no action on days before today
      (this.withDateRange && this.day.dayState === DayState.Blocked && !!this.bookingDate && !this.day.isPickupDate) || // no coloring days outside possible range
      (this.withDateRange && !!this.bookingDate && !!this.endDate && !inRange && !this.day.isPickupDate); // no coloring days outside selected range

    const availablePartially = (available || inRange) && this.day.dayState === DayState.SelectablePartially;

    const classes = {
      Day: true,
      Today: this.day.isToday,
      Available: available,
      NotAvailable: notAvailable,
      Empty: empty,
      NotAllowed: notAllowed,
      DayInRange: dayInRange,
      NotAllowedHoverRange: notAllowedHoverRange,
      NotAvailableHoverRange: notAvailableHoverRange,
      LastHoverRange: lastHoverRange,
      Selected: selected,
      Hovered: hovered,
      ClickToReset: this.day.clickToReset,
    };

    const outerClasses = {
      DayWrapper: true,
      NoTransition: true,
      InRange: this.day.isInRange,
      InRangeLeftRound: this.day.inRangeLeftRound,
      InRangeRightRound: this.day.inRangeRightRound,
      InHoverRange: this.day.isInHoverRange,
      InHoverRangeLeftRound: this.day.inHoverRangeLeftRound,
      InHoverRangeRightRound: this.day.inHoverRangeRightRound,
      InRangeNotAvailable: this.day.isInHoverRange && this.day.dayState === DayState.Blocked,
      InRangeLastPossible: lastHoverRange,
    };

    let tooltipText = '';
    if (notAllowed && this.calendarMode !== CalendarMode.SingleDayDropOff) tooltipText = t('articleBooking.noPickup');
    if (notAllowed && this.calendarMode === CalendarMode.SingleDayDropOff) tooltipText = t('articleBooking.noDropOff');
    if (notAvailable) tooltipText = t('articleBooking.notAvailable');
    if (notAllowedHoverRange) tooltipText = t('articleBooking.noDropOff');
    if (notAvailableHoverRange) tooltipText = t('articleBooking.outsidePossibleRange');
    if (availablePartially) tooltipText = t('articleBooking.availablePartially');

    const tooltipAllowed: boolean = notAllowed || notAvailable || notAllowedHoverRange || notAvailableHoverRange || availablePartially;
    const showAdditionalStyle = availablePartially;

    const dayClick = (day: DayConfig) => {
      if (tooltipAllowed) {
        this.showTooltip = true;
      }

      this.onDayClick(day);
    };

    const dayHover = (day: DayConfig) => {
      if (tooltipAllowed) {
        this.showTooltip = true;
      }

      this.onDayHover(day);
    };

    const dayHoverEnd = () => {
      this.showTooltip = false;
    };

    return (
      <Host style={this.index === 0 ? { gridColumn: '' + this.firstDayColumn(this.secondMonth) } : {}} class={outerClasses}>
        <button
          class={classes} //
          onClick={() => dayClick(this.day)}
          onMouseOver={() => dayHover(this.day)}
          onMouseOut={dayHoverEnd}
        >
          {showAdditionalStyle && (
            <div
              class={{
                DayAddition: true, //
                AvailablePartially: availablePartially,
                AvailablePartiallyHoverRange: dayInRange,
              }}
            >
              <time dateTime={format(date, 'P')}>{getDate(date)}</time>
            </div>
          )}
          {this.showTooltip && (
            <Fragment>
              <div class="Tooltip">{tooltipText}</div>
              <div class="TooltipMustache" />
            </Fragment>
          )}
          <time
            class={{
              TimeContainer: true, //
              TimeContainerAvailable: available && !empty && !notAllowed,
              TimeContainerSelected: selected && !empty && !notAllowed,
            }}
            dateTime={format(date, 'P')}
          >
            {getDate(date)}
          </time>
        </button>
      </Host>
    );
  }
}
